import React, { useCallback, useEffect, useRef, useState } from 'react'
import DayPicker, { DayModifiers } from 'react-day-picker'
import 'react-day-picker/lib/style.css'
import './DatePicker.scss'
import moment from 'moment'
import { monthArray, generateYearArray } from './dateUtils'

interface DatePickerProps {
  selectedDate: Date | undefined
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | undefined>>
}

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const FLYOUT_ID = 'wedding-date-flyout'

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <path d="M0.5 5.5H15.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M14 2.5H2C1.17 2.5 0.5 3.17 0.5 4V13C0.5 13.83 1.17 14.5 2 14.5H14C14.83 14.5 15.5 13.83 15.5 13V4C15.5 3.17 14.83 2.5 14 2.5Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M4.5 0.5V2.5M11.5 0.5V2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const Caret = ({ direction }: { direction: 'prev' | 'next' }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d={direction === 'prev' ? 'M15 6L9 12L15 18' : 'M9 6L15 12L9 18'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const DatePicker = (props: DatePickerProps) => {
  const { selectedDate, setSelectedDate } = props

  const [open, setOpen] = useState(false)
  const [displayMonth, setDisplayMonth] = useState<Date>(startOfMonth(selectedDate ?? new Date()))
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const flyoutRef = useRef<HTMLDivElement>(null)

  const today = new Date()
  const firstMonth = startOfMonth(today)
  const yearOptions = generateYearArray(10)
  const lastMonth = new Date(parseInt(yearOptions[yearOptions.length - 1].value), 11, 1)
  const lastDay = new Date(lastMonth.getFullYear(), 11, 31)
  const canGoBack = displayMonth > firstMonth
  const canGoForward = displayMonth < lastMonth

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  const toggle = () => {
    if (open) {
      close(false)
      return
    }
    setDisplayMonth(startOfMonth(selectedDate ?? new Date()))
    setOpen(true)
  }

  const handleDateClick = (date: Date, modifier?: DayModifiers) => {
    if (modifier?.disabled) return
    setSelectedDate(date)
    close(true)
  }

  const showMonth = (offset: number) => {
    setDisplayMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1))
  }

  useEffect(() => {
    if (!open) return
    const flyout = flyoutRef.current
    const day =
      flyout?.querySelector<HTMLElement>('.DayPicker-Day--selected:not(.DayPicker-Day--outside)') ??
      flyout?.querySelector<HTMLElement>('.DayPicker-Day[tabindex="0"]')
    ;(day ?? flyout)?.focus()

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [open, close])

  const handleFlyoutKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close(true)
    }
  }

  const formattedDate = selectedDate ? moment(selectedDate).format('MM/DD/YYYY') : ''

  return (
    <div className="date-picker" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`date-input ${formattedDate ? 'has-value' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={FLYOUT_ID}
        aria-label={formattedDate ? `Wedding date, ${formattedDate}` : 'Wedding date'}
        onClick={toggle}
      >
        <span className="date-input-text">{formattedDate || 'Wedding date'}</span>
        <span className="date-input-icon">
          <CalendarIcon />
        </span>
      </button>

      {open && (
        <div
          id={FLYOUT_ID}
          ref={flyoutRef}
          className="date-flyout"
          role="dialog"
          aria-label="Choose your wedding date"
          tabIndex={-1}
          onKeyDown={handleFlyoutKeyDown}
        >
          <div className="date-flyout-header">
            <div className="date-flyout-caption" aria-live="polite">
              {monthArray[displayMonth.getMonth()].label} {displayMonth.getFullYear()}
            </div>
            <div className="date-flyout-nav">
              <button
                type="button"
                className="date-flyout-caret"
                aria-label="Previous month"
                disabled={!canGoBack}
                onClick={() => showMonth(-1)}
              >
                <Caret direction="prev" />
              </button>
              <button
                type="button"
                className="date-flyout-caret"
                aria-label="Next month"
                disabled={!canGoForward}
                onClick={() => showMonth(1)}
              >
                <Caret direction="next" />
              </button>
            </div>
          </div>
          <DayPicker
            className="Selectable"
            selectedDays={selectedDate}
            month={displayMonth}
            fromMonth={firstMonth}
            toMonth={lastMonth}
            onMonthChange={(month) => setDisplayMonth(startOfMonth(month))}
            onDayClick={handleDateClick}
            navbarElement={() => <></>}
            captionElement={() => <></>}
            weekdaysShort={WEEKDAY_INITIALS}
            showOutsideDays
            disabledDays={[{ before: today }, { after: lastDay }]}
          />
        </div>
      )}
    </div>
  )
}

export default DatePicker
